import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { UserContext } from "../context/UserContext";
import { AuthContext } from "../context/AuthContext";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons";
import NavSub from "../components/NavSub";
import isGuestUser from "../utils/guestUser";

import ToastComponent from "../components/ToastComponent";
import { clientApiUrl, taskApiUrl } from "../utils/GetUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../Styles/VoucherReports.css";
import {
  CaretCircleDoubleLeft,
  CaretCircleDoubleRight,
  FileArrowUp,
  FunnelSimple,
} from "phosphor-react";
import * as XLSX from "xlsx";

const VoucherReports = () => {
  // Search functionality
  const [query, setQuery] = useState("");
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const debounceTimeout = useRef(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchSuggestionByText = async () => {
    if (!token) {
      console.error("Token is missing.");
      return;
    }
    try {
      const response = await fetch(
        `${clientApiUrl}/GetAllClient?SearchByText=${query}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorised access. Please login again.",
          "error",
        );
        await logout();
        return;
      }

      const handleAuthError = handleAuthExpiry(response);
      if (handleAuthError === true) {
        toastRef.current.showToast("Your Session has expired", "error");
        logout();
        return false;
      }
      if (!response.ok) {
        console.error("Failed to fetch suggestions:", response.statusText);
        setSearchLoading(false);
        return;
      }
      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    if (query.length > 0) {
      setSearchLoading(true);

      debounceTimeout.current = setTimeout(() => {
        fetchSuggestionByText();
      }, 1000);
    } else {
      setSuggestions([]);
      setSearchLoading(false);
    }

    return () => {
      clearTimeout(debounceTimeout.current);
    };
  }, [query]);

  const handleCustomersearchClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleClearAllSearch = () => {
    setQuery("");
    setPage(1);
    setSelectedSuggestions([]);
  };

  const handleCheckboxChange = (suggestion) => {
    if (selectedSuggestions?.length === 0) {
      setPage(1);
    }
    setSelectedSuggestions((prevSelected) => {
      if (prevSelected?.some((item) => item.Id === suggestion.Id)) {
        return prevSelected?.filter((item) => item.Id !== suggestion.Id);
      } else {
        return [suggestion];
      }
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownsearchRef.current &&
        !dropdownsearchRef.current.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };

    const handleScroll = () => {
      setIsSearchOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Main component logic
  const {
    teamsData,
    setTeamsData,
    teamNames,
    setTeamNames,
    usersList,
    fetchTeams,
  } = useContext(UserContext);
  const { token, logout, handleAuthExpiry, userData } = useContext(AuthContext);

  const [teams, setTeams] = useState([]);
  const [isAccountResponsibleOpen, setIsAccountResponsibleOpen] = useState();
  const [accSearch, setAccSearch] = useState("");
  const [accountResponsible, setAccountResponsible] = useState([]);
  const [accountResponsible2, setAccountResponsible2] = useState([]);
  const [selectedAccountResponsible, setSelectedAccountResponsible] = useState(
    [],
  );
  const dropdownsearchRef = useRef(null);
  const toastRef = useRef();
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([userData?.[0]]);
  const [allActivities, setAllActivities] = useState([]);

  const formatDateToYYYYMM = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const todaysDate = new Date();
  const [selectedYear, setSelectedYear] = useState(todaysDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    formatDateToYYYYMM(todaysDate),
  );
  const [selectedDay, setSelectedDay] = useState(todaysDate.getDate());
  const [isTableLoaded, setIsTableLoaded] = useState(false);

  // Initialize teams
  useEffect(() => {
    if (teamNames) {
      const filteredTeams = teamNames.filter(
        (team) =>
          team?.superTeamId === 0 &&
          team?.type === "T" &&
          team?.status === true,
      );
      setTeams(filteredTeams);
    }
  }, [teamNames]);

  // Initialize account responsible data
  useEffect(() => {
    setAccountResponsible(usersList);
    setAccountResponsible2(usersList);
  }, [usersList]);

  // Handle preparer selection
  const handleCheckboxAccountResponsibleChange = (acc) => {
    setSelectedAccountResponsible([acc]); // Single selection

    // Update selectedEmployees based on both teams AND preparer selection
    if (selectedTeams?.length > 0) {
      const teamFilteredEmployees = usersList?.filter((user) =>
        selectedTeams?.includes(parseInt(user?.STM_Team)),
      );
      const combinedEmployees = [...teamFilteredEmployees];
      if (!teamFilteredEmployees.some((user) => user.EM_ID === acc.EM_ID)) {
        combinedEmployees.push(acc);
      }
      setSelectedEmployees(combinedEmployees);
    } else {
      setSelectedEmployees([acc]);
    }
  };

  // Clear preparer selection
  const clearPreparerSelection = () => {
    setSelectedAccountResponsible([]);
    if (selectedTeams?.length) {
      const filteredEmployees = usersList?.filter((user) =>
        selectedTeams?.includes(parseInt(user?.STM_Team)),
      );
      setSelectedEmployees(filteredEmployees);
    } else {
      setSelectedEmployees([userData?.[0]]);
    }
  };

  // Handle team selection
  const handleChange = (event) => {
    if (event.target.value?.[event.target.value?.length - 1] === undefined) {
      setSelectedTeams([]);
      if (selectedAccountResponsible.length > 0) {
        setSelectedEmployees(selectedAccountResponsible);
      } else {
        setSelectedEmployees([userData?.[0]]);
      }
      return;
    }

    setSelectedTeams(event.target.value);

    // Update employees based on both teams and preparer
    const teamFilteredEmployees = usersList?.filter((user) =>
      event.target.value?.includes(parseInt(user?.STM_Team)),
    );

    if (selectedAccountResponsible.length > 0) {
      const combinedEmployees = [...teamFilteredEmployees];
      selectedAccountResponsible.forEach((acc) => {
        if (!teamFilteredEmployees.some((user) => user.EM_ID === acc.EM_ID)) {
          combinedEmployees.push(acc);
        }
      });
      setSelectedEmployees(combinedEmployees);
    } else {
      setSelectedEmployees(teamFilteredEmployees);
    }
  };

  // Handle coordination between teams and preparer filters
  useEffect(() => {
    if (!selectedTeams?.length) {
      if (selectedAccountResponsible.length > 0) {
        setSelectedEmployees(selectedAccountResponsible);
      } else {
        setSelectedEmployees([userData?.[0]]);
      }
    } else {
      const filteredEmployees = usersList?.filter((user) =>
        selectedTeams?.includes(parseInt(user?.STM_Team)),
      );

      if (selectedAccountResponsible.length > 0) {
        const combinedEmployees = [...filteredEmployees];
        selectedAccountResponsible.forEach((acc) => {
          if (!filteredEmployees.some((user) => user.EM_ID === acc.EM_ID)) {
            combinedEmployees.push(acc);
          }
        });
        setSelectedEmployees(combinedEmployees);
      } else {
        setSelectedEmployees(filteredEmployees);
      }
    }
  }, [selectedTeams, usersList, token, selectedAccountResponsible]);

  // Handle clear all teams
  const handleClearAll = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setSelectedTeams([]);
    if (selectedAccountResponsible.length > 0) {
      setSelectedEmployees(selectedAccountResponsible);
    } else {
      setSelectedEmployees([userData?.[0]]);
    }
  };

  const toggleTeamSelector = () => {
    // setTeamSelectorOpen((prev) => !prev);
  };

  const handleAccountingResponsibleClick = () => {
    setIsAccountResponsibleOpen((prevState) => !prevState);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownsearchRef.current &&
        !dropdownsearchRef.current.contains(event.target) &&
        !event.target.closest(".Title-STR-main2")
      ) {
        setIsAccountResponsibleOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownsearchRef]);

  const [expanded, setExpanded] = useState(true);
  const [projectData, setProjectData] = useState("");

  // Fetch project list
  const fetchProjectList = async () => {
    try {
      const response = await fetch(`${clientApiUrl}/GetAllProjectList`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorised access. Please login again.",
          "error",
        );
        await logout();
        return;
      }

      const handleAuthError = handleAuthExpiry(response);
      if (handleAuthError === true) {
        toastRef.current.showToast("Your Session has expired", "error");
        logout();
        return false;
      }
      if (response.ok) {
        const data = await response.json();
        setProjectData(data);
      } else {
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (token) {
      fetchProjectList(token);
    }
  }, [token]);

  // Fetch all activities
  const fetchAllActivities = async () => {
    try {
      const response = await fetch(
        `${clientApiUrl}/GetAllActivity?Page=1&ItemsPerPage=100`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorised access. Please login again.",
          "error",
        );
        await logout();
        return;
      }

      const handleAuthError = handleAuthExpiry(response);
      if (handleAuthError === true) {
        toastRef.current.showToast("Your Session has expired", "error");
        logout();
        return false;
      }
      if (response.ok) {
        const data = await response.json();
        setAllActivities(
          data?.filter(
            (act) => act?.AL_Type === "Vouchers" && act?.AL_Status == "1",
          ),
        );
        return data;
      } else {
        console.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAllActivities();
    }
  }, [token]);

  // Data fetching
  const [page, setPage] = useState(1);
  const [masterData, setMasterData] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [end, setEnd] = useState(false);
  const fetchInProgress = useRef(false);

  const formatSelectedEmployees = (selected) => {
    if (!selected?.length) return "";
    const emIds = selected.map((item) => `'${item["EM_ID"]}'`);
    return emIds.join(",");
  };

  const formatDate = (day, month, year) => {
    const formattedDay = day.toString().padStart(2, "0");
    const formattedMonth = month.toString().padStart(2, "0");
    return `${year}-${formattedMonth}-${formattedDay}`;
  };

  const fetchCustomer = async (dateStart, dateEnd, first) => {
    if (!token) return;
    const clientId = selectedSuggestions?.[0]?.Id;
    if (!allActivities?.length) return;

    if (loading || fetchInProgress.current) return;
    fetchInProgress.current = true;
    setLoading(true);

    const today = new Date();
    const day = selectedDay || today.getDate();
    let month = selectedMonth || today.getMonth() + 1;
    const year = selectedYear || today.getFullYear();

    if (typeof month === "string" && month.includes("-")) {
      month = month.split("-")[1];
    }
    month = month.toString().padStart(2, "0");

    const date = formatDate(day, month, year);
    try {
      const response = await fetch(
        `${clientApiUrl}/GetAllClient?Page=${first ? 1 : page}&ItemsPerPage=${
          clientId ? 1 : 20
        }&CreatDate=${date}${
          clientId
            ? `&id=${clientId}`
            : selectedEmployees.length > 0
              ? `&SubOwnerEMIDs=${formatSelectedEmployees(selectedEmployees)}`
              : ""
        }`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorised access. Please login again.",
          "error",
        );
        await logout();
        return;
      }

      const handleAuthError = handleAuthExpiry(response);
      if (handleAuthError === true) {
        toastRef.current.showToast("Your Session has expired", "error");
        logout();
        return false;
      }

      if (response.ok) {
        const data = await response.json();

        if (data.length === 0) {
          setEnd(true);
        } else {
          const formattedIds = data.map((d) => `'${d?.Id}'`).join(", ");
          await fetchedReportsPerClient(data, formattedIds, dateStart, dateEnd);
        }
      } else {
        setLoading(false);
        setEnd(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  };

  const fetchedReportsPerClient = async (
    clientData,
    ids,
    dateStart,
    dateEnd,
  ) => {
    if (!ids || !dateStart || !dateEnd) return;

    try {
      const response = await fetch(
        `${taskApiUrl}/GetTaskTotalTime?ClientIds=${ids}&DateRange1=${dateStart}&DateRange2=${dateEnd}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorised access. Please login again.",
          "error",
        );
        await logout();
        return;
      }

      const handleAuthError = handleAuthExpiry(response);
      if (handleAuthError === true) {
        toastRef.current.showToast("Your Session has expired", "error");
        logout();
        return false;
      }

      if (response.ok) {
        const data = await response.json();

        const currentMergedData = clientData.map((client) => {
          const matchingReport = data.find(
            (report) => report.ClientId === client.Id,
          );

          const mergedClient = { ...client };
          let total = 0;

          allActivities.forEach((activity) => {
            const activityReport = data.find(
              (report) =>
                report.ActivityId === activity.AL_ID &&
                report.ClientId === client.Id,
            );

            const activityTime = activityReport
              ? parseFloat(activityReport.TotalTime)
              : 0;
            mergedClient[activity.AL_Name] = activityTime.toFixed(2);
            total += activityTime;

            if (activityReport?.ActiveSalesforcecodes?.length) {
              mergedClient[`${activity.AL_Name}_SalesforceCodes`] =
                activityReport.ActiveSalesforcecodes.map(
                  (sf) => sf.SalesforceCode,
                ).join(", ");

              mergedClient[`${activity.AL_Name}_TotalSalesforceCode`] =
                activityReport.ActiveSalesforcecodes.map(
                  (sf) => sf.TotalSalesforceCode,
                ).join(", ");
            } else {
              mergedClient[`${activity.AL_Name}_SalesforceCodes`] = "";
              mergedClient[`${activity.AL_Name}_TotalSalesforceCode`] = "";
            }
          });

          mergedClient.total = total.toFixed(2);

          return {
            ...mergedClient,
            ...matchingReport,
          };
        });

        setMergedData((prevData) => [...prevData, ...currentMergedData]);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  const handleScroll = useCallback(() => {
    const container = document.querySelector(".scroll-voucherreports");
    if (!container || loading || end) return;

    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;

    // Check if we're near the bottom (within 100px)
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom) {
      setPage((prev) => prev + 1);
    }
  }, [loading, end]);

  const isInitialRender = useRef(true);

  const getStartAndEndDates = (monthString) => {
    const [year, month] = monthString?.split("-");
    const firstDay = `${year}-${month}-01`;
    const lastDay = new Date(year, parseInt(month, 10), 0).getDate();
    const lastDateString = `${year}-${month}-${lastDay
      .toString()
      .padStart(2, "0")}`;

    return { firstDay, lastDay: lastDateString };
  };

  useEffect(() => {
    const monthString = selectedMonth;
    const { firstDay, lastDay } = getStartAndEndDates(monthString);
    if (!selectedEmployees?.length) {
      setSelectedEmployees([userData?.[0]]);
    }
    fetchCustomer(firstDay, lastDay, false); // Add false for "first" parameter
  }, [page, allActivities, selectedEmployees]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    setEnd(false);
    setPage(1);
    setMergedData([]);
    setMasterData([]);

    const employeesToUse =
      selectedEmployees.length > 0 ? selectedEmployees : [userData?.[0]];

    const monthString = `${selectedMonth.toString().padStart(2, "0")}`;
    const { firstDay, lastDay } = getStartAndEndDates(monthString);
    fetchCustomer(firstDay, lastDay, true, employeesToUse);
  }, [
    selectedMonth,
    selectedAccountResponsible,
    selectedSuggestions,
    selectedTeams,
  ]);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setDate(1);
    return today.toISOString().split("T")[0];
  });

  const getUserFirstName = (emId) => {
    const user = usersList?.find((user) => user.EM_ID === emId);
    return user ? user.EM_FirstName + " " + user.EM_LastName : "";
  };

  const getUserTeamName = (emId) => {
    const user = usersList?.find((user) => user.EM_ID === emId);
    return user
      ? teamNames?.find((team) => team.id === parseFloat(user.STM_Team))
          ?.name || "Unknown Team"
      : "Unknown Team";
  };

  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownload = async () => {
    if (isGuestUser(userData?.[0]?.DRM_ID)) {
      toastRef.current.showToast(`Guest User Does not have Add or Edit Access`);
      return;
    }

    try {
      setIsDownloading(true);

      if (!mergedData || mergedData.length === 0) {
        toastRef.current.showToast("No data available to download", "warning");
        return;
      }

      const excelData = mergedData.map((client, index) => {
        const rowData = {
          "Customer Name": client?.Name || "",
          "Project Number": client?.ProjectNumber || "",
          "Prepare Name": getUserFirstName(client?.SubOwner),
          "ERP System": client?.ERPSystem || "",
          "Cost Center":
            selectedSuggestions?.length > 0
              ? client?.CostCenterName
              : client?.CostCenter || "",
          Department:
            selectedSuggestions?.length > 0
              ? client?.DepartmentName
              : client?.Department || "",
          "Department Head": client?.HODName || "",
          "View Account Owner": client?.OwnerEMId || "",
          "View Team Lead": client?.LeadId || "",
          "Synergy Accounting responsible": getUserFirstName(client?.SubOwner),
          "Synergy Quality Consultant": getUserFirstName(client?.SubQC),
          "Synergy Process Lead": getUserFirstName(client?.SubProcLead),
          "Synergy Department Lead": getUserFirstName(client?.SubLead),
          "Synergy Team": getUserTeamName(client?.SubOwner),
          "Frequency Voucher Processing": client?.ReportingFrequencyName || "",
          "Bank Remittance Type": client?.RemittanceTypeName || "",
        };

        allActivities.forEach((activity) => {
          rowData[activity.AL_Name] = client?.[activity.AL_Name] || "0.00";

          if (client?.[`${activity.AL_Name}_SalesforceCodes`]) {
            rowData[`${activity.AL_Name} - Salesforce Codes`] =
              client[`${activity.AL_Name}_SalesforceCodes`];
          }
          if (client?.[`${activity.AL_Name}_TotalSalesforceCode`]) {
            rowData[`${activity.AL_Name} - Total Salesforce Code`] =
              client[`${activity.AL_Name}_TotalSalesforceCode`];
          }
        });

        rowData["Total Time"] = client?.total || "0.00";
        rowData["Volume"] = client?.ActiveSalesforcecodes?.length
          ? client.ActiveSalesforcecodes.find(
              (sf) =>
                sf.SalesforceCode?.toLowerCase() ===
                "0201 vouchers".toLowerCase(),
            )?.TotalSalesforceCode || 0
          : 0;

        return rowData;
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      XLSX.utils.book_append_sheet(workbook, worksheet, "Voucher Reports");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const teamNames = selectedTeams
        .map((teamId) => {
          const team = teams.find((t) => t.id === teamId);
          return team?.name || "Unknown";
        })
        .join("_");

      const preparerNames =
        selectedAccountResponsible.length > 0
          ? selectedAccountResponsible
              .map((acc) => `${acc.EM_FirstName}_${acc.EM_LastName}`)
              .join("_")
          : "All_Preparers";

      const filename = `Voucher_Reports_${selectedMonth}_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);

      toastRef.current.showToast(
        `Report downloaded successfully with ${excelData.length} records`,
        "success",
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toastRef.current.showToast("Error generating Excel file", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const spinnerStyle = {
    width: "14px",
    height: "14px",
    border: "2px solid #ccc",
    borderTop: "2px solid #3d025f",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  };

  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const container = document.querySelector(".scroll-voucherreports");
    if (!container) return;

    // Add throttling to prevent too many calls
    let ticking = false;

    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", throttledHandleScroll);

    return () => {
      container.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [handleScroll]);

  return (
    <div>
      <NavSub />
      <ToastComponent ref={toastRef} timeout={4000} />

      <div
        style={{
          display: "flex",
          gap: "60px",
          padding: "20px 24px",
          width: "98%",
          margin: "1rem auto",
          background: "white",
          borderRadius: "10px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "28px" }}>
          {/* Team Selector */}
          <FormControl className="select-dropdown-user">
            <Select
              labelId="team-selector-label"
              id="team-selector"
              multiple
              value={selectedTeams}
              onChange={handleChange}
              disabled={selectedAccountResponsible.length > 0}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <span
                      style={{
                        color: "#000",
                        fontFamily: "Poppins",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Select Teams
                    </span>
                  );
                }
                return selected
                  .map((teamId) => {
                    const team = teams.find((team) => team.id === teamId);
                    return team?.name || "Unknown Team";
                  })
                  .join(", ");
              }}
              style={{ width: "150px" }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                  },
                },
              }}
            >
              {selectedTeams?.length > 0 && (
                <MenuItem onClick={handleClearAll}>
                  <ListItemText style={{ color: "red" }} primary="Clear All" />
                </MenuItem>
              )}

              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  <Checkbox checked={selectedTeams.indexOf(team.id) > -1} />
                  <ListItemText primary={team.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preparer Selector */}
          <div
            className="Title-STR-main2"
            onClick={(e) => {
              if (selectedTeams.length === 0) {
                handleAccountingResponsibleClick();
              } else {
                e.stopPropagation(); // prevent dropdown from opening
              }
            }}
            style={{
              pointerEvents: selectedTeams.length > 0 ? "none" : "auto",
              opacity: selectedTeams.length > 0 ? 0.5 : 1,
              cursor: selectedTeams.length > 0 ? "not-allowed" : "pointer",
            }}
          >
            {selectedAccountResponsible.length > 0
              ? `${selectedAccountResponsible[0].EM_FirstName} ${selectedAccountResponsible[0].EM_LastName}`
              : "Preparer Name"}
            <FontAwesomeIcon
              className="str-caret-icon"
              icon={isAccountResponsibleOpen ? faCaretUp : faCaretDown}
            />
            {isAccountResponsibleOpen && (
              <div ref={dropdownsearchRef} className="search-drop-Report">
                <div className="search-drop-page">
                  <div className="main-suggestion">
                    <div className="input-suggestion">
                      <div className="icon-search-phospher">
                        <MagnifyingGlass
                          size={20}
                          color="#ada4a4"
                          weight="fill"
                        />
                      </div>
                      <input
                        type="text"
                        value={accSearch}
                        className="input-suggestion-search"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const value = e.target.value.toLowerCase();
                          setAccSearch(value);
                          setAccountResponsible2(
                            accountResponsible?.filter((acc) =>
                              (acc.EM_FirstName + " " + acc?.EM_LastName)
                                .toLowerCase()
                                .includes(value),
                            ),
                          );
                        }}
                        placeholder="Start typing..."
                      />
                    </div>
                    <div tabIndex={1}>
                      <div className="flex justify-between">
                        <div
                          style={{
                            color: "red",
                            padding: "2px 22px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearPreparerSelection();
                          }}
                        >
                          Clear Preparer
                        </div>
                      </div>
                      {accountResponsible2?.map((acc) => (
                        <div
                          key={acc.EM_ID}
                          className="suggestion-list-li"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            className="radio-searchbox"
                            style={{
                              accentColor: "rgb(80, 20, 90)",
                              width: "18px",
                              height: "18px",
                            }}
                            checked={selectedAccountResponsible?.some(
                              (item) => item.EM_ID === acc.EM_ID,
                            )}
                            onChange={() =>
                              handleCheckboxAccountResponsibleChange(acc)
                            }
                          />
                          <label className="li-content">
                            {acc.EM_FirstName + " " + acc.EM_LastName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Date Selector */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <input
              type="month"
              className="date-design-VoucherReport"
              label="Select Date"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Download Button */}
        <div
          className="Nav-btn-user"
          onClick={!isDownloading ? handleDownload : null}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: isDownloading ? "not-allowed" : "pointer",
          }}
        >
          {isDownloading ? (
            <>
              <div style={spinnerStyle}></div>
              <button className="Nav-Sub-btn" style={{ opacity: 0.6 }} disabled>
                DOWNLOADING...
              </button>
            </>
          ) : (
            <>
              <FileArrowUp size={25} color="#3d025f" weight="fill" />
              <button className="Nav-Sub-btn">DOWNLOAD FILES</button>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          width: "98%",

          margin: "0px 10px 0px 10px",
          overflow: "auto",
          maxHeight: "calc(100vh - 250px)",
        }}
        className="scroll-voucherreports custom-scrollbar"
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 999,
                  left: 0,
                  background: "#d9d9d9",
                }}
                className="header-customername"
              >
                <div
                  style={{
                    display: "flex",
                    // justifyContent: "space-between",
                    alignItems: "center",
                    position: "sticky",
                    position: "sticky",
                    left: 0,
                    gap: "16px",
                  }}
                >
                  Customer Name
                  <FunnelSimple
                    className="icon-search-dropdown"
                    onClick={handleCustomersearchClick}
                    weight="bold"
                    size={20}
                    color="#061347"
                  />
                </div>
                {isSearchOpen && (
                  <div
                    ref={dropdownsearchRef}
                    className="search-drop-design-report"
                    style={{
                      marginLeft: "140px !important",
                      marginTop: "23px !important",
                    }}
                  >
                    <div className="search-drop-page">
                      <div className="main-suggestion">
                        <div className="input-suggestion">
                          <div className="icon-search-phospher">
                            <MagnifyingGlass
                              size={20}
                              color="#ada4a4"
                              weight="fill"
                            />{" "}
                          </div>

                          <input
                            type="text"
                            value={query}
                            className="input-suggestion-search"
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Start typing..."
                          />

                          <button
                            className="clear-title"
                            onClick={handleClearAllSearch}
                          >
                            Clear All
                          </button>
                        </div>
                        {loading ? (
                          <p className="suggestion-title">
                            <CircularProgress
                              width={30}
                              style={{ color: "purple" }}
                            />
                          </p>
                        ) : (
                          <div style={{ maxHeight: "500px" }} tabIndex={1}>
                            {suggestions.map((suggestion) => (
                              <div
                                key={suggestion.Id}
                                className="suggestion-list-li"
                              >
                                <input
                                  type="checkbox"
                                  className="check-searchbox"
                                  checked={selectedSuggestions.some(
                                    (item) => item.Id === suggestion.Id,
                                  )}
                                  onChange={() =>
                                    handleCheckboxChange(suggestion)
                                  }
                                />
                                <label className="li-content">
                                  {suggestion.Name}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </th>
              <th
                style={{
                  backgroundColor: "#d9d9d9",
                  color: "#000",
                  position: "relative",
                  minWidth: "300px",
                  textAlign: "start",
                  position: "sticky",
                  top: 0,
                  left: "300px",
                  zIndex: 997,
                }}
              >
                {" "}
                Project Number
                <span
                  style={{
                    position: "absolute",
                    minWidth: "150px",
                    right: "-120px",
                    top: "16px",
                    color: "black",
                    cursor: "pointer",
                    zIndex: "999",
                  }}
                  onClick={() => setExpanded(!expanded)}
                >
                  {" "}
                  {expanded ? (
                    <CaretCircleDoubleRight size={32} color="#080808" />
                  ) : (
                    <CaretCircleDoubleLeft size={32} color="#080808" />
                  )}
                </span>
              </th>
              <th
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#d9d9d9",
                  left: "600px", // Adjust this value based on your layout
                  zIndex: 997,
                  minWidth: "200px",
                }}
              >
                Prepare Name
              </th>
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  ERP System
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Cost Center
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Department
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Department Head{" "}
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  View Account Owner
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  View Team Lead{" "}
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Synergy Accounting responsible
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Synergy Quality Consultant{" "}
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Synergy Process Lead
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Synergy Department Lead
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Synergy Team
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Frequency Voucher Processing
                </th>
              )}
              {expanded && (
                <th
                  className="Expand-styles"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 10,
                  }}
                >
                  Bank Remittance Type
                </th>
              )}

              {allActivities?.map((ac) => {
                return (
                  <th
                    className="last-portion-style"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    {" "}
                    {ac.AL_Name}
                  </th>
                );
              })}
              <th
                className="last-portion-style"
                style={{ position: "sticky", top: 0, zIndex: 10 }}
              >
                Total Time
              </th>
              <th
                className="last-portion-style"
                style={{ position: "sticky", top: 0, zIndex: 10 }}
              >
                Volume
              </th>
            </tr>
          </thead>
          <tbody>
            {mergedData?.map((data, index) => {
              {
                /* { mergedData
              ?.filter(i=>parseInt(i?.total)!=0)?. map((data, index) => { */
              }
              return (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#F1F1F1" : "#FFFFFF", // Alternating row colors
                  }}
                >
                  <td
                    style={{
                      padding: "15px",

                      fontFamily: "Poppins",
                      position: "sticky",
                      background: index % 2 === 0 ? "#F1F1F1" : "#FFFFFF",
                      left: 0,
                    }}
                  >
                    {data?.Name}
                  </td>
                  <td
                    style={{
                      position: "sticky",
                      left: "300px",
                      padding: "15px",

                      fontFamily: "Poppins",
                      background: index % 2 === 0 ? "#F1F1F1" : "#FFFFFF",
                    }}
                  >
                    {data?.ProjectNumber}
                  </td>
                  <td
                    style={{
                      position: "sticky",
                      left: "600px", // Should match the left value from the header
                      padding: "15px",

                      fontFamily: "Poppins",
                      background: index % 2 === 0 ? "#F1F1F1" : "#FFFFFF",
                    }}
                  >
                    {getUserFirstName(data?.SubOwner)}
                  </td>
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {data?.ERPSystem}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {selectedSuggestions?.length > 0
                        ? data?.CostCenterName
                        : data?.CostCenter}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {selectedSuggestions?.length > 0
                        ? data?.DepartmentName
                        : data?.Department}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {data?.HODName}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {data?.OwnerEMId}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {data?.LeadId}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {getUserFirstName(data?.SubOwner)}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {getUserFirstName(data?.SubQC)}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {getUserFirstName(data?.SubProcLead)}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {getUserFirstName(data?.SubLead)}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {getUserTeamName(data?.SubOwner)}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {" "}
                      {data?.ReportingFrequencyName}
                    </td>
                  )}
                  {expanded && (
                    <td
                      style={{
                        padding: "15px",
                        border: "1px solid #fff",
                        fontFamily: "Poppins",
                        color: "#898989",
                      }}
                    >
                      {data?.RemittanceTypeName}
                    </td>
                  )}
                  {allActivities.map((activity) => (
                    <td key={activity.AL_ID} style={{ fontFamily: "Poppins" }}>
                      {data?.[activity.AL_Name] || "0.00"}
                    </td>
                  ))}
                  <td
                    style={{
                      padding: "15px",
                      border: "1px solid #fff",
                      fontFamily: "Poppins",
                      color: "#898989",
                    }}
                  >
                    {data?.total}
                  </td>
                  <td
                    style={{
                      padding: "15px",
                      border: "1px solid #fff",
                      fontFamily: "Poppins",
                      color: "#898989",
                    }}
                  >
                    {data?.ActiveSalesforcecodes?.length
                      ? data.ActiveSalesforcecodes.find(
                          (sf) =>
                            sf.SalesforceCode?.toLowerCase() ===
                            "0201 vouchers".toLowerCase(),
                        )?.TotalSalesforceCode || 0
                      : 0}
                  </td>
                </tr>
                // ))}
              );
            })}
          </tbody>
        </table>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <CircularProgress style={{ color: "purple" }} width={40} />
          </div>
        )}
        {end && <div>No more Data...</div>}
      </div>
    </div>
  );
};

export default VoucherReports;
